import { motion } from 'framer-motion';
import { Clock, MapPin, Play } from 'lucide-react';

export const Services = () => {
  return (
    <div data-testid="services-page" className="min-h-screen">
      <section 
        className="py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #CD853F 0%, #DAA520 50%, #B8860B 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '500px',
            backgroundPosition: 'center',
            backgroundRepeat: 'repeat'
          }}
        />

        <div className="absolute bottom-20 right-20 text-white/5 transform rotate-12">
          <svg width="180" height="180" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="8" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-body font-medium tracking-widest uppercase text-white/90">Service Times</span>
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mt-4 drop-shadow-lg">
              Experience Heaven on Earth
            </h1>
            <p className="text-xl text-white/95 mt-6 max-w-3xl mx-auto">
              More than a service—it's an encounter. Join us for explosive worship, life-transforming teaching, and the tangible presence of God that changes everything.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid="service-time-card"
              className="bg-white/90 backdrop-blur-sm rounded-3xl p-10 border-l-4 border-primary shadow-2xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-amber-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Clock className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-heading font-bold text-foreground mb-2">Sunday Worship</h2>
                  <p className="text-4xl font-heading font-bold text-primary">11:00 AM</p>
                </div>
              </div>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Every Sunday, lives are changed, miracles happen, and faith explodes. This isn't church as usual—it's a divine appointment where Heaven invades earth and nothing is impossible with God.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <div className="w-2 h-2 bg-primary rounded-full" />
                  <span>Dynamic Worship Experience</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <div className="w-2 h-2 bg-primary rounded-full" />
                  <span>Anointed Preaching</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <div className="w-2 h-2 bg-primary rounded-full" />
                  <span>Children's Ministry Available</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid="location-card"
              className="bg-white/90 backdrop-blur-sm rounded-3xl p-10 border-l-4 border-secondary shadow-2xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-secondary to-green-700 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-heading font-bold text-foreground mb-2">Location</h2>
                  <p className="text-xl text-muted-foreground">Belton, Missouri</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-lg font-semibold text-foreground mb-1">Address</p>
                  <p className="text-muted-foreground">
                    5700 East 191st Street<br />
                    Belton, MO 64012
                  </p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-foreground mb-1">Contact</p>
                  <p className="text-muted-foreground">
                    Phone: <a href="tel:8168760470" className="text-primary hover:underline">(816) 876-0470</a>
                  </p>
                </div>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=5700+East+191st+Street+Belton+MO+64012"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="get-directions-button"
                  className="inline-block bg-secondary text-secondary-foreground px-6 py-3 rounded-full font-semibold hover:bg-secondary/90 transition-all duration-300 shadow-lg mt-4"
                >
                  Get Directions
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section 
        data-testid="livestream-section" 
        className="py-20 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #8B7355 0%, #A0826D 50%, #8B7355 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_earthy-interact/artifacts/nz39lewe_IMG_20260129_120644.png')",
            backgroundSize: '600px',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />

        <div className="absolute top-10 left-10 text-white/5 transform -rotate-12">
          <svg width="150" height="150" viewBox="0 0 100 100">
            <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="10" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-sm font-body font-medium tracking-widest uppercase text-amber-300">Can't Make It?</span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mt-4 mb-6">
              Watch Services Online
            </h2>
            <p className="text-xl text-white/95 max-w-3xl mx-auto">
              Join us virtually every Sunday at 11:00 AM. Experience the same powerful worship and teaching from wherever you are.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl overflow-hidden shadow-2xl max-w-4xl mx-auto border-4 border-white/20"
          >
            <div className="aspect-w-16 aspect-h-9 bg-gradient-to-br from-primary/30 to-secondary/30 backdrop-blur-sm">
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <div className="w-24 h-24 bg-white/90 rounded-full flex items-center justify-center mb-6 hover:scale-110 transition-transform duration-300 cursor-pointer shadow-2xl">
                  <Play className="w-12 h-12 text-primary ml-1" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-white mb-3">
                  Livestream Sundays at 11:00 AM
                </h3>
                <p className="text-white/90 mb-6">
                  Click to watch our live service
                </p>
                <button
                  data-testid="watch-live-button"
                  className="bg-white/95 text-primary px-8 py-3 rounded-full font-semibold hover:bg-white transition-all duration-300 hover:shadow-2xl"
                >
                  Watch Live Stream
                </button>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 text-center"
          >
            <h3 className="text-2xl font-heading font-bold text-white mb-6">
              What to Expect
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div data-testid="expect-card-1" className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <h4 className="font-heading font-semibold text-foreground text-lg mb-2">Casual & Welcoming</h4>
                <p className="text-muted-foreground text-sm">
                  Come as you are. We're a friendly community that values authenticity.
                </p>
              </div>
              <div data-testid="expect-card-2" className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <h4 className="font-heading font-semibold text-foreground text-lg mb-2">Spirit-Filled Worship</h4>
                <p className="text-muted-foreground text-sm">
                  Experience passionate worship that creates an atmosphere for God's presence.
                </p>
              </div>
              <div data-testid="expect-card-3" className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <h4 className="font-heading font-semibold text-foreground text-lg mb-2">Biblical Teaching</h4>
                <p className="text-muted-foreground text-sm">
                  Receive practical, life-changing messages rooted in Scripture.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
